import { env } from '@/lib/env'
import { slugOf } from '@/lib/address'

/**
 * Which address the app was opened at.
 *
 * Every tenant works at its own subdomain — masar.localhost in development —
 * and the bare domain is the platform's (the console lives at /admin). The
 * app reads the address once, at start-up, to decide which it is: the
 * platform's, a workspace's, or unknown (P12's positive host validation —
 * the platform is never "whatever is not a workspace").
 *
 * At a workspace's address the path picks between the company's public
 * website (the root) and its workspace (under /app).
 *
 * The address only picks the screens. Whether an address is a live company's,
 * and whether the signed-in user may see its workspace, is decided by the
 * API: an unknown host answers 404 there, and membership is checked on every
 * request. The label rule itself is address.ts's (TenantHost's mirror).
 */

/** The workspace slug a hostname names, or null when it names none (see addressKind() for what it is instead). */
export function workspaceSlug(hostname: string, baseDomain: string = env.baseDomain): string | null {
  return slugOf(hostname, baseDomain)
}

/** What an address is: the platform's own, a workspace's subdomain, or nobody's. */
export type AddressKind = 'platform' | 'workspace' | 'unknown'

/**
 * The platform is the base domain or `<alias>.<base>` for one of its aliases
 * (tenancy.platform_aliases); a workspace is one other valid, unreserved label
 * under the base; anything else — a reserved or nested label, another domain,
 * an IP address — is unknown. The API has the last word: an unclaimed label
 * answers 404 there.
 */
export function addressKind(hostname: string, baseDomain: string = env.baseDomain, aliases: readonly string[] = env.platformAliases): AddressKind {
  const host = hostname.toLowerCase().replace(/\.$/, '')

  if (baseDomain !== '' && (host === baseDomain || aliases.some((alias) => host === `${alias}.${baseDomain}`))) return 'platform'

  return workspaceSlug(host, baseDomain) === null ? 'unknown' : 'workspace'
}

/** Whether an address is only a door to the platform's (`www.<base>`): documents go to the base, path kept. */
export function isPlatformDoor(hostname: string, baseDomain: string = env.baseDomain, aliases: readonly string[] = env.platformAliases): boolean {
  return aliases.includes('www') && hostname.toLowerCase().replace(/\.$/, '') === `www.${baseDomain}`
}

/** What the address this tab was opened at is, by its name alone: the router's fallback when GET /api/address can't be asked (P12). */
export const currentAddress: AddressKind = typeof window === 'undefined' ? 'unknown' : addressKind(window.location.hostname)

/**
 * The key a workspace remembers this browser's choices under (the demo banner, the navigation's folds, what's
 * new): its subdomain's label or — at a custom domain (P12), the host itself, the `address.host` GET /api/address
 * answered for. Null at the platform's address.
 */
export const currentWorkspace: string | null =
  typeof window === 'undefined' || currentAddress === 'platform'
    ? null
    : currentAddress === 'workspace'
      ? workspaceSlug(window.location.hostname)
      : window.location.hostname.toLowerCase().replace(/\.$/, '')

/** The platform's own address, keeping this tab's scheme and port. */
export function platformUrl(path = '/'): string {
  const { protocol, port } = window.location

  return `${protocol}//${env.baseDomain}${port ? `:${port}` : ''}${path}`
}

/** Where a company's workspace lives at its address; the root is its public website. */
export const WORKSPACE_APP = '/app'

/** Whether a path at a workspace's address belongs to the workspace rather than the website. */
export function isWorkspaceApp(pathname: string): boolean {
  return pathname === WORKSPACE_APP || pathname.startsWith(`${WORKSPACE_APP}/`)
}
